import React, { useState } from 'react';
import { Public as PublicIcon } from '@mui/icons-material';
import {
  Box,
  Typography,
  Paper,
  Container,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  People as PeopleIcon,
  Shield as ShieldIcon,
  Analytics as AnalyticsIcon,
  AttachMoney as MoneyIcon,
  Campaign as CampaignIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as RbacIcon,
  Support as SupportIcon,
  AutoAwesome as AIOpsIcon,
  HowToReg as AccessIcon,
  Article as ResourcesIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
// Tab Components
import UserManagementTab from '../components/admin/UserManagementTab';
import ContentModerationTab from '../components/admin/ContentModerationTab';
import TrustSafetyTab from '../components/admin/TrustSafetyTab';
import PlatformAnalyticsTab from '../components/admin/PlatformAnalyticsTab';
import SiteAnalyticsTab from '../components/admin/SiteAnalyticsTab';
import FinancialManagementTab from '../components/admin/FinancialManagementTab';
import AdvertisementManagementTab from '../components/admin/AdvertisementManagementTab';
import SystemManagementTab from '../components/admin/SystemManagementTab';
import StaffPermissionsTab from '../components/admin/StaffPermissionsTab';
import SupportTicketsTab from '../components/admin/SupportTicketsTab';
import NotificationsTab from '../components/admin/NotificationsTab';
import AIOperationsTab from '../components/admin/AIOperationsTab';
import PlanRequestsTab from '../components/admin/PlanRequestsTab';
import AccessRequestsTab from '../components/admin/AccessRequestsTab';
import ResourcesTab from '../components/admin/ResourcesTab';
import LegalDocumentsTab from '../components/admin/LegalDocumentsTab';
import GavelIcon from '@mui/icons-material/Gavel';
import DashboardHomeTab from '../components/admin/DashboardHomeTab';
import AuditComplianceTab from '../components/admin/AuditComplianceTab';

const AdminDashboardPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0); // Default to Home
  const [systemSubTab, setSystemSubTab] = useState<number | undefined>(undefined);

  const tabs = [
    { label: 'Home', icon: <DashboardIcon />, component: <DashboardHomeTab />, group: '' },
    { label: 'Users', icon: <PeopleIcon />, component: <UserManagementTab />, group: 'Subscribers' },
    { label: 'Access', icon: <AccessIcon />, component: <AccessRequestsTab />, group: 'Subscribers' },
    { label: 'Feature Flags', icon: <SettingsIcon />, component: <SystemManagementTab lockedView="flags" />, group: 'Product & Marketing' },
    { label: 'Plans', icon: <MoneyIcon />, component: <PlanRequestsTab />, group: 'Product & Marketing' },
    { label: 'Ads', icon: <CampaignIcon />, component: <AdvertisementManagementTab />, group: 'Product & Marketing' },
    { label: 'Resources', icon: <ResourcesIcon />, component: <ResourcesTab />, group: 'Product & Marketing' },
    { label: 'Site Analytics', icon: <PublicIcon />, component: <SiteAnalyticsTab />, group: 'Product & Marketing' },
    { label: 'Legal', icon: <GavelIcon />, component: <LegalDocumentsTab />, group: 'Product & Marketing' },
    { label: 'Financial', icon: <MoneyIcon />, component: <FinancialManagementTab />, group: 'Business & Operations' },
    { label: 'AI Ops', icon: <AIOpsIcon />, component: <AIOperationsTab />, group: 'Business & Operations' },
    { label: 'Analytics', icon: <AnalyticsIcon />, component: <PlatformAnalyticsTab />, group: 'Business & Operations' },
    { label: 'System', icon: <SettingsIcon />, component: <SystemManagementTab />, group: 'Platform' },
    { label: 'Moderation', icon: <ShieldIcon />, component: <ContentModerationTab />, group: 'Platform' },
    { label: 'Trust & Safety', icon: <ShieldIcon />, component: <TrustSafetyTab />, group: 'Platform' },
    { label: 'Notifications', icon: <CampaignIcon />, component: <NotificationsTab />, group: 'Platform' },
    { label: 'Staff & RBAC', icon: <RbacIcon />, component: <StaffPermissionsTab />, group: 'Platform' },
    { label: 'Support', icon: <SupportIcon />, component: <SupportTicketsTab />, group: 'Platform' },
    { label: 'Audit & Compliance', icon: <GavelIcon />, component: <AuditComplianceTab />, group: 'Platform' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={600} color="primary">
            Admin Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage users, content, analytics, and system settings
          </Typography>
        </Box>

        {/* Sidebar nav + content */}
        <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          {/* Mobile: dropdown selector */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Section"
              value={activeTab}
              onChange={(e) => setActiveTab(Number(e.target.value))}
            >
              {tabs.map((tab, index) => (
                <MenuItem key={index} value={index}>{tab.label}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
            {/* Desktop: vertical sidebar */}
            <List
              component="nav"
              disablePadding
              sx={{
                display: { xs: 'none', md: 'block' },
                width: 220,
                flexShrink: 0,
                borderRight: '1px solid',
                borderColor: 'divider',
                py: 1,
                bgcolor: '#FAFAF8',
              }}
            >
              {tabs.map((tab, index) => {
                const selected = activeTab === index;
                const prevGroup = index > 0 ? tabs[index - 1].group : null;
                const showHeader = tab.group && tab.group !== prevGroup;
                return (
                  <React.Fragment key={index}>
                    {showHeader && (
                      <Typography sx={{ px: 2.5, pt: 2, pb: 0.5, fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#8AA3A0" }}>
                        {tab.group}
                      </Typography>
                    )}
                    <ListItemButton
                      selected={selected}
                      onClick={() => setActiveTab(index)}
                      sx={{
                        mx: 1,
                        my: 0.25,
                        borderRadius: 2,
                        color: selected ? "#fff" : "text.primary",
                        bgcolor: selected ? "#2D6E6A" : "transparent",
                        "&.Mui-selected": { bgcolor: "#2D6E6A", "&:hover": { bgcolor: "#1F4E4A" } },
                        "&:hover": { bgcolor: selected ? "#1F4E4A" : "rgba(45,110,106,0.08)" },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, color: selected ? "#fff" : "#2D6E6A" }}>
                        {tab.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={tab.label}
                        primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: selected ? 600 : 500 }}
                      />
                    </ListItemButton>
                  </React.Fragment>
                );
              })}
            </List>

            {/* Active tab content */}
            <Box sx={{ flex: 1, p: 3, minWidth: 0 }}>
              {activeTab === 0 ? <DashboardHomeTab onNavigate={(label: string, subTab?: number) => { const i = tabs.findIndex(t => t.label === label); if (i >= 0) { setActiveTab(i); if (subTab !== undefined) setSystemSubTab(subTab); } }} /> : (tabs[activeTab]?.label === 'System' ? <SystemManagementTab initialSubTab={systemSubTab} /> : tabs[activeTab]?.label === 'Feature Flags' ? <SystemManagementTab lockedView="flags" /> : tabs[activeTab]?.component)}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminDashboardPage;
